// Dashboard API Service

const API_URL = (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? 'http://127.0.0.1:8000/api' : window.location.origin + '/api';

// Enhanced error handling
class ApiError extends Error {
  constructor(message, status, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

// Dashboard API service class with proper error handling
class DashboardApiService {
  constructor() {
    this.baseURL = API_URL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  // Private method for making HTTP requests with proper error handling
  async _makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      headers: this.defaultHeaders,
      cache: 'no-cache',
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData?.detail || errorData?.message || `Request failed with status ${response.status}`,
          response.status,
          errorData
        );
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error.message || 'Network error occurred',
        0,
        { originalError: error }
      );
    }
  }

  // Build query string from parameters
  _buildQueryString(params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });
    return searchParams.toString();
  }

  /**
   * Get session statistics for a specific session
   * @param {string} sessionId - Session identifier
   * @returns {Promise<Object>} Session statistics including aggregated data, subject stats, and question stats
   */
  async getSessionStats(sessionId) {
    console.log('🌐 API: getSessionStats called with sessionId:', sessionId);
    
    if (!sessionId) {
      throw new ApiError('Session ID is required', 400);
    }

    const queryParams = this._buildQueryString({
      session_id: sessionId
    });
    
    const result = await this._makeRequest(`/get_session_stats?${queryParams}`);
    console.log('📦 API: getSessionStats response:', result);
    return result;
  }
}

// Create and export a singleton instance
const dashboardApi = new DashboardApiService();

// Export both the class and the instance for flexibility
export { DashboardApiService, ApiError };
export default dashboardApi;
